const DEG = Math.PI / 180;
const DOGLEG_EPS = 1e-7;

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function toNumber(value) {
  const n = parseFloat(value);
  return Number.isFinite(n) ? n : null;
}

/** Mining convention: dip 0 = horizontal, negative = down. Azimuth 0 = north, 90 = east. */
export function directionENU(dipDeg, azimuthDeg) {
  const dip = dipDeg * DEG;
  const az = azimuthDeg * DEG;
  const cosDip = Math.cos(dip);
  return {
    ve: cosDip * Math.sin(az),
    vn: cosDip * Math.cos(az),
    vu: Math.sin(dip),
  };
}

export function minCurvatureDelta(dir1, dir2, measuredDepth) {
  const md = measuredDepth;
  if (!(md > 0) || !dir1 || !dir2) {
    return { dE: 0, dN: 0, dU: 0 };
  }
  const dot = clamp(dir1.ve * dir2.ve + dir1.vn * dir2.vn + dir1.vu * dir2.vu, -1, 1);
  const beta = Math.acos(dot);
  const rf = beta < DOGLEG_EPS ? 1 : (2 / beta) * Math.tan(beta / 2);
  const k = (md / 2) * rf;
  return {
    dE: k * (dir1.ve + dir2.ve),
    dN: k * (dir1.vn + dir2.vn),
    dU: k * (dir1.vu + dir2.vu),
  };
}

function surveyAzimuth(row) {
  return toNumber(row?.NAT_Azimuth);
}

function surveyDip(row) {
  return toNumber(row?.Dip);
}

function surveyDepth(row) {
  return toNumber(row?.Depth);
}

function surveyPriority(row) {
  const n = toNumber(row?.Priority);
  return n == null ? 0 : n;
}

/**
 * Lowest Priority wins at a given Depth. Collar station is injected at depth 0
 * when missing. Last attitude is extended to Max_Depth when surveys stop short.
 */
export function buildSurveyStations(collar, surveyRows = []) {
  const collarDip = toNumber(collar?.Collar_Dip);
  const collarAz = toNumber(collar?.Collar_Azimuth);
  const maxDepth = toNumber(collar?.Max_Depth);
  const byDepth = new Map();

  (surveyRows || []).forEach((row) => {
    const depth = surveyDepth(row);
    const dip = surveyDip(row);
    const azimuth = surveyAzimuth(row);
    if (depth == null || depth < 0 || dip == null || azimuth == null) return;
    const prev = byDepth.get(depth);
    const priority = surveyPriority(row);
    if (!prev || priority < prev.priority) {
      byDepth.set(depth, { depth, dip, azimuth, priority });
    }
  });

  const stations = Array.from(byDepth.values()).sort((a, b) => a.depth - b.depth);

  if (collarDip != null && collarAz != null) {
    const hasCollar = stations.length > 0 && stations[0].depth === 0;
    if (!hasCollar) {
      stations.unshift({ depth: 0, dip: collarDip, azimuth: collarAz, priority: -1 });
    }
  }

  if (maxDepth != null && stations.length) {
    const last = stations[stations.length - 1];
    if (maxDepth > last.depth) {
      stations.push({
        depth: maxDepth,
        dip: last.dip,
        azimuth: last.azimuth,
        priority: last.priority,
      });
    }
  }

  return stations;
}

export function groupSurveysByHole(surveys = []) {
  const map = new Map();
  (surveys || []).forEach((row) => {
    const holeId = row?.Hole_ID;
    if (!holeId) return;
    if (!map.has(holeId)) map.set(holeId, []);
    map.get(holeId).push(row);
  });
  return map;
}

export function surveysForHole(surveysByHole, hole) {
  const holeId = hole?.Hole_ID;
  if (!holeId || !surveysByHole) return [];
  const rows = surveysByHole.get(holeId) || [];
  const dataset = hole?.DataSet;
  if (dataset == null || String(dataset).trim() === '') return rows;
  const matched = rows.filter(
    (row) => row?.DataSet == null || String(row.DataSet) === String(dataset)
  );
  return matched.length ? matched : rows;
}

/**
 * Scene coords match DatabaseHoleRender: x=east, y=RL, z=originNorth-north.
 * origin is getOriginERL() → [east, rl, north].
 */
export function desurveyHoleToScene(collar, surveyRows, origin) {
  const east0 = toNumber(collar?.NAT_East);
  const north0 = toNumber(collar?.NAT_North);
  const rl0 = toNumber(collar?.NAT_RL);
  if (east0 == null || north0 == null || rl0 == null) return [];

  const originEast = origin?.[0] || 0;
  const originRl = origin?.[1] || 0;
  const originNorth = origin?.[2] || 0;

  const toScene = (east, north, rl, depth) => ({
    x: east - originEast,
    y: rl - originRl,
    z: originNorth - north,
    depth,
  });

  const stations = buildSurveyStations(collar, surveyRows);
  if (stations.length < 2) {
    const maxDepth = toNumber(collar?.Max_Depth);
    const dip = toNumber(collar?.Collar_Dip);
    const az = toNumber(collar?.Collar_Azimuth);
    if (maxDepth == null || dip == null || az == null) {
      return [toScene(east0, north0, rl0, 0)];
    }
    const dir = directionENU(dip, az);
    return [
      toScene(east0, north0, rl0, 0),
      toScene(east0 + dir.ve * maxDepth, north0 + dir.vn * maxDepth, rl0 + dir.vu * maxDepth, maxDepth),
    ];
  }

  let east = east0;
  let north = north0;
  let rl = rl0;
  const points = [toScene(east, north, rl, stations[0].depth)];

  for (let i = 0; i < stations.length - 1; i += 1) {
    const a = stations[i];
    const b = stations[i + 1];
    const md = b.depth - a.depth;
    if (!(md > 0)) continue;
    const delta = minCurvatureDelta(directionENU(a.dip, a.azimuth), directionENU(b.dip, b.azimuth), md);
    east += delta.dE;
    north += delta.dN;
    rl += delta.dU;
    points.push(toScene(east, north, rl, b.depth));
  }

  return points.length >= 2 ? points : [toScene(east0, north0, rl0, 0)];
}

export function positionAtDepth(points, depth) {
  if (!points?.length) return null;
  const d = Number(depth);
  if (!Number.isFinite(d)) return points[0];
  if (d <= points[0].depth) return points[0];
  const last = points[points.length - 1];
  if (d >= last.depth) return last;

  for (let i = 0; i < points.length - 1; i += 1) {
    const a = points[i];
    const b = points[i + 1];
    if (d < a.depth || d > b.depth) continue;
    const span = b.depth - a.depth;
    const t = span > 0 ? (d - a.depth) / span : 0;
    return {
      x: a.x + (b.x - a.x) * t,
      y: a.y + (b.y - a.y) * t,
      z: a.z + (b.z - a.z) * t,
      depth: d,
    };
  }

  return last;
}
